import React from 'react';
import { AISolution } from '../../config/api';
import './SolutionCard.css';

export interface SolutionCardProps {
  solution: AISolution;
  showFullDescription?: boolean;
  showFeatures?: boolean;
  showPricing?: boolean;
  showCompanyInfo?: boolean;
  onViewClick?: (solution: AISolution) => void;
  onDemoClick?: (solution: AISolution) => void;
  onWebsiteClick?: (solution: AISolution) => void;
  className?: string;
}

const SolutionCard: React.FC<SolutionCardProps> = ({
  solution,
  showFullDescription = false,
  showFeatures = true,
  showPricing = true,
  showCompanyInfo = true,
  onViewClick,
  onDemoClick,
  onWebsiteClick,
  className = '',
}) => {
  const handleViewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onViewClick) {
      onViewClick(solution);
    }
  };

  const handleDemoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDemoClick) {
      onDemoClick(solution);
    } else if (solution.demo_url) {
      window.open(solution.demo_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleWebsiteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onWebsiteClick) {
      onWebsiteClick(solution);
    } else {
      window.open(solution.website_url, '_blank', 'noopener,noreferrer');
    }
  };

  const formatPrice = (price: number | undefined, model: string) => {
    if (!price) return model;
    
    const formattedPrice = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);

    switch (model.toLowerCase()) {
      case 'subscription':
      case 'monthly':
        return `${formattedPrice}/month`;
      case 'annual':
      case 'yearly':
        return `${formattedPrice}/year`;
      case 'per_user':
        return `${formattedPrice}/user`;
      case 'usage_based':
        return `From ${formattedPrice}`;
      case 'one_time':
        return formattedPrice;
      default:
        return `${formattedPrice}`;
    }
  };

  const displayDescription = showFullDescription 
    ? solution.description 
    : solution.short_description || solution.description;

  return (
    <div 
      className={`solution-card ${solution.featured ? 'solution-card--featured' : ''} ${solution.verified ? 'solution-card--verified' : ''} ${className}`}
      onClick={handleViewClick}
    >
      {/* Header */}
      <header className="solution-card__header">
        {/* Logo and Title */}
        <div className="solution-card__title-section">
          {solution.logo_url && (
            <div className="solution-card__logo">
              <img
                src={solution.logo_url}
                alt={`${solution.name} logo`}
                loading="lazy"
                onError={(e) => {
                  // Hide logo if it fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          )}
          
          <div className="solution-card__title-content">
            <h3 className="solution-card__title">
              {solution.name}
              {solution.verified && (
                <svg
                  className="solution-card__verified-badge"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <title>Verified Solution</title>
                  <path d="M9 12l2 2 4-4" />
                  <circle cx="12" cy="12" r="9" />
                </svg>
              )}
              {solution.featured && (
                <span className="solution-card__featured-badge">
                  Featured
                </span>
              )}
            </h3>
            
            {showCompanyInfo && (
              <div className="solution-card__company">
                <span className="solution-card__company-name">
                  {solution.company_name}
                </span>
                <span className="solution-card__category">
                  {solution.category}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Pricing */}
        {showPricing && (
          <div className="solution-card__pricing">
            <div className="solution-card__price">
              {solution.pricing_model === 'free' ? (
                <span className="solution-card__price-free">Free</span>
              ) : (
                <span className="solution-card__price-value">
                  {formatPrice(solution.starting_price, solution.pricing_model)}
                </span>
              )}
            </div>
            {solution.free_trial && (
              <span className="solution-card__trial">
                Free Trial
              </span>
            )}
          </div>
        )}
      </header>

      {/* Description */}
      <div className="solution-card__content">
        <p className="solution-card__description">
          {displayDescription}
        </p>
      </div>

      {/* Features */}
      {showFeatures && solution.features && solution.features.length > 0 && (
        <div className="solution-card__features">
          <h4 className="solution-card__features-title">Key Features:</h4>
          <ul className="solution-card__features-list">
            {solution.features.slice(0, 4).map((feature, index) => (
              <li key={index} className="solution-card__feature">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20,6 9,17 4,12" />
                </svg>
                {feature}
              </li>
            ))}
            {solution.features.length > 4 && (
              <li className="solution-card__feature solution-card__feature--more">
                +{solution.features.length - 4} more features
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Use Cases */}
      {solution.use_cases && solution.use_cases.length > 0 && (
        <div className="solution-card__use-cases">
          <div className="solution-card__use-cases-list">
            {solution.use_cases.slice(0, 3).map((useCase, index) => (
              <span key={index} className="solution-card__use-case">
                {useCase}
              </span>
            ))}
            {solution.use_cases.length > 3 && (
              <span className="solution-card__use-case solution-card__use-case--more">
                +{solution.use_cases.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="solution-card__footer">
        <div className="solution-card__stats">
          {solution.view_count > 0 && (
            <span className="solution-card__stat" title="Views">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              {solution.view_count}
            </span>
          )}
          
          {solution.click_count > 0 && (
            <span className="solution-card__stat" title="Clicks">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15,3 21,3 21,9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              {solution.click_count}
            </span>
          )}
        </div>

        <div className="solution-card__actions">
          {solution.demo_url && (
            <button
              className="solution-card__action solution-card__action--demo"
              onClick={handleDemoClick}
              title="Try Demo"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5,3 19,12 5,21 5,3" />
              </svg>
              Demo
            </button>
          )}
          
          <button
            className="solution-card__action solution-card__action--website"
            onClick={handleWebsiteClick}
            title="Visit Website"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15,3 21,3 21,9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Visit
          </button>
        </div>
      </footer>
    </div>
  );
};

export default SolutionCard;
